/**
 * Polygon triangulation (ear clipping with hole bridging) for floor / counter
 * regions, which are non-convex (alcoves) and may carry holes (walls).
 *
 * A compact, dependency-free port of the mapbox `earcut` algorithm (ISC) minus
 * the z-order hash optimization — the floor-plan polygons are tiny, so plain
 * O(n²) clipping is more than fast enough. Input is a multipolygon ring list
 * (ring[0] outer, rest holes); output is flat [x,y] triples in plan space.
 */

import { Point } from '@kaiizen/kai-floor-plan';

interface Node {
  i: number;
  x: number;
  y: number;
  prev: Node;
  next: Node;
  steiner: boolean;
}

/** Triangulate one polygon (outer ring + holes) into plan-space triangles. */
export function triangulatePolygon(rings: Point[][]): Point[][] {
  if (rings.length === 0 || rings[0].length < 3) return [];
  const data: number[] = [];
  const holeIndices: number[] = [];
  for (let r = 0; r < rings.length; r++) {
    if (r > 0) holeIndices.push(data.length / 2);
    for (const p of rings[r]) data.push(p.x, p.y);
  }
  const tris = earcut(data, holeIndices);
  const out: Point[][] = [];
  for (let i = 0; i < tris.length; i += 3) {
    out.push([
      { x: data[tris[i] * 2], y: data[tris[i] * 2 + 1] },
      { x: data[tris[i + 1] * 2], y: data[tris[i + 1] * 2 + 1] },
      { x: data[tris[i + 2] * 2], y: data[tris[i + 2] * 2 + 1] },
    ]);
  }
  return out;
}

function earcut(data: number[], holeIndices: number[]): number[] {
  const hasHoles = holeIndices.length > 0;
  const outerLen = hasHoles ? holeIndices[0] * 2 : data.length;
  let outerNode = linkedList(data, 0, outerLen, true);
  const triangles: number[] = [];
  if (!outerNode || outerNode.next === outerNode.prev) return triangles;
  if (hasHoles) outerNode = eliminateHoles(data, holeIndices, outerNode);
  earcutLinked(outerNode, triangles);
  return triangles;
}

function linkedList(data: number[], start: number, end: number, clockwise: boolean): Node | null {
  let last: Node | null = null;
  const ccw = signedArea(data, start, end) > 0;
  if (clockwise === ccw) {
    for (let i = start; i < end; i += 2) last = insertNode(i / 2, data[i], data[i + 1], last);
  } else {
    for (let i = end - 2; i >= start; i -= 2) last = insertNode(i / 2, data[i], data[i + 1], last);
  }
  if (last && equals(last, last.next)) {
    removeNode(last);
    last = last.next;
  }
  return last;
}

function earcutLinked(ear: Node | null, triangles: number[], pass = 0): void {
  if (!ear) return;
  let stop = ear;
  let next: Node;
  while (ear!.prev !== ear!.next) {
    const prev = ear!.prev;
    next = ear!.next;
    if (isEar(ear!)) {
      triangles.push(prev.i, ear!.i, next.i);
      removeNode(ear!);
      ear = next.next;
      stop = next.next;
      continue;
    }
    ear = next;
    if (ear === stop) {
      // No ear found — try a fallback once before giving up.
      if (pass === 0) earcutLinked(cureLocalIntersections(filterPoints(ear)!, triangles), triangles, 1);
      break;
    }
  }
}

function isEar(ear: Node): boolean {
  const a = ear.prev, b = ear, c = ear.next;
  if (area(a, b, c) >= 0) return false; // reflex
  let p = ear.next.next;
  while (p !== ear.prev) {
    if (pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) && area(p.prev, p, p.next) >= 0) return false;
    p = p.next;
  }
  return true;
}

function cureLocalIntersections(start: Node, triangles: number[]): Node {
  let p = start;
  do {
    const a = p.prev, b = p.next.next;
    if (!equals(a, b) && intersects(a, p, p.next, b) && locallyInside(a, b) && locallyInside(b, a)) {
      triangles.push(a.i, p.i, b.i);
      removeNode(p);
      removeNode(p.next);
      p = start = b;
    }
    p = p.next;
  } while (p !== start);
  return filterPoints(p)!;
}

function eliminateHoles(data: number[], holeIndices: number[], outerNode: Node): Node {
  const queue: Node[] = [];
  for (let i = 0; i < holeIndices.length; i++) {
    const start = holeIndices[i] * 2;
    const end = i < holeIndices.length - 1 ? holeIndices[i + 1] * 2 : data.length;
    const list = linkedList(data, start, end, false);
    if (list) {
      if (list === list.next) list.steiner = true;
      queue.push(getLeftmost(list));
    }
  }
  queue.sort((a, b) => a.x - b.x);
  let node = outerNode;
  for (const hole of queue) node = eliminateHole(hole, node);
  return node;
}

function eliminateHole(hole: Node, outerNode: Node): Node {
  const bridge = findHoleBridge(hole, outerNode);
  if (!bridge) return outerNode;
  const bridgeReverse = splitPolygon(bridge, hole);
  filterPoints(bridgeReverse, bridgeReverse.next);
  return filterPoints(bridge, bridge.next)!;
}

function findHoleBridge(hole: Node, outerNode: Node): Node | null {
  let p = outerNode;
  const hx = hole.x, hy = hole.y;
  let qx = -Infinity;
  let m: Node | null = null;
  do {
    if (hy <= p.y && hy >= p.next.y && p.next.y !== p.y) {
      const x = p.x + ((hy - p.y) / (p.next.y - p.y)) * (p.next.x - p.x);
      if (x <= hx && x > qx) {
        qx = x;
        m = p.x < p.next.x ? p : p.next;
        if (x === hx) return m;
      }
    }
    p = p.next;
  } while (p !== outerNode);
  if (!m) return null;
  const stop = m;
  const mx = m.x, my = m.y;
  let tanMin = Infinity;
  p = m;
  do {
    if (
      hx >= p.x && p.x >= mx && hx !== p.x
      && pointInTriangle(hy < my ? hx : qx, hy, mx, my, hy < my ? qx : hx, hy, p.x, p.y)
    ) {
      const tan = Math.abs(hy - p.y) / (hx - p.x);
      if (locallyInside(p, hole) && (tan < tanMin || (tan === tanMin && (p.x > m!.x || (p.x === m!.x && sectorContainsSector(m!, p)))))) {
        m = p;
        tanMin = tan;
      }
    }
    p = p.next;
  } while (p !== stop);
  return m;
}

function sectorContainsSector(m: Node, p: Node): boolean {
  return area(m.prev, m, p.prev) < 0 && area(p.next, m, m.next) < 0;
}

function getLeftmost(start: Node): Node {
  let p = start, leftmost = start;
  do {
    if (p.x < leftmost.x || (p.x === leftmost.x && p.y < leftmost.y)) leftmost = p;
    p = p.next;
  } while (p !== start);
  return leftmost;
}

function pointInTriangle(ax: number, ay: number, bx: number, by: number, cx: number, cy: number, px: number, py: number): boolean {
  return (cx - px) * (ay - py) >= (ax - px) * (cy - py)
    && (ax - px) * (by - py) >= (bx - px) * (ay - py)
    && (bx - px) * (cy - py) >= (cx - px) * (by - py);
}

function area(p: Node, q: Node, r: Node): number {
  return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
}
function equals(p1: Node, p2: Node): boolean {
  return p1.x === p2.x && p1.y === p2.y;
}

function intersects(p1: Node, q1: Node, p2: Node, q2: Node): boolean {
  const o1 = Math.sign(area(p1, q1, p2));
  const o2 = Math.sign(area(p1, q1, q2));
  const o3 = Math.sign(area(p2, q2, p1));
  const o4 = Math.sign(area(p2, q2, q1));
  return (o1 !== o2 && o3 !== o4);
}

function locallyInside(a: Node, b: Node): boolean {
  return area(a.prev, a, a.next) < 0
    ? area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0
    : area(a, b, a.prev) < 0 || area(a, a.next, b) < 0;
}

function filterPoints(start: Node | null, end?: Node): Node | null {
  if (!start) return start;
  if (!end) end = start;
  let p = start, again: boolean;
  do {
    again = false;
    if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) {
      removeNode(p);
      p = end = p.prev;
      if (p === p.next) break;
      again = true;
    } else {
      p = p.next;
    }
  } while (again || p !== end);
  return end;
}

function splitPolygon(a: Node, b: Node): Node {
  const a2: Node = { i: a.i, x: a.x, y: a.y, prev: null!, next: null!, steiner: false };
  const b2: Node = { i: b.i, x: b.x, y: b.y, prev: null!, next: null!, steiner: false };
  const an = a.next, bp = b.prev;
  a.next = b;
  b.prev = a;
  a2.next = an;
  an.prev = a2;
  b2.next = a2;
  a2.prev = b2;
  bp.next = b2;
  b2.prev = bp;
  return b2;
}

function insertNode(i: number, x: number, y: number, last: Node | null): Node {
  const p: Node = { i, x, y, prev: null!, next: null!, steiner: false };
  if (!last) {
    p.prev = p;
    p.next = p;
  } else {
    p.next = last.next;
    p.prev = last;
    last.next.prev = p;
    last.next = p;
  }
  return p;
}

function removeNode(p: Node): void {
  p.next.prev = p.prev;
  p.prev.next = p.next;
}

function signedArea(data: number[], start: number, end: number): number {
  let sum = 0;
  for (let i = start, j = end - 2; i < end; i += 2) {
    sum += (data[j] - data[i]) * (data[i + 1] + data[j + 1]);
    j = i;
  }
  return sum;
}
